import { useRef, useState } from "react";
import { adminApi, extractApiError } from "../../lib/adminApi";
import { Upload, X, Image as ImageIcon } from "lucide-react";

/**
 * Image picker — shows current image (if any), lets the admin upload a new
 * one (POST /api/admin/upload) and calls `onChange(url)` with the returned
 * `/api/uploads/<file>` path. Also supports clearing the value.
 */
export function ImageUploadPicker({
    value,
    onChange,
    label,
    hint,
    testId = "image-picker",
}) {
    const inputRef = useRef(null);
    const [state, setState] = useState({ status: "idle", error: "" });

    const submit = async (file) => {
        if (!file) return;
        setState({ status: "uploading", error: "" });
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await adminApi.post("/admin/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            onChange(data.url);
            setState({ status: "idle", error: "" });
        } catch (err) {
            setState({
                status: "error",
                error: extractApiError(err, "Téléversement échoué."),
            });
        }
    };

    const onFile = (e) => submit(e.target.files?.[0]);

    return (
        <div className="mb-6">
            {label && <div className="eyebrow mb-2">{label}</div>}
            {hint && (
                <p className="text-xs text-neutral-500 mb-3 leading-relaxed">
                    {hint}
                </p>
            )}
            <div className="flex items-start gap-4 flex-wrap">
                <div
                    data-testid={`${testId}-preview`}
                    className="w-40 h-40 border border-ink-borderStrong bg-ink-surface flex items-center justify-center overflow-hidden"
                >
                    {value ? (
                        <img
                            src={value}
                            alt="Aperçu"
                            className="max-w-full max-h-full object-contain"
                        />
                    ) : (
                        <ImageIcon
                            size={28}
                            className="text-neutral-600"
                            strokeWidth={1.3}
                        />
                    )}
                </div>
                <div className="flex-1 min-w-[220px] space-y-3">
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                        className="hidden"
                        onChange={onFile}
                        data-testid={`${testId}-input`}
                    />
                    <div className="flex gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            disabled={state.status === "uploading"}
                            className="btn-outline-gold"
                            data-testid={`${testId}-upload-btn`}
                        >
                            <Upload size={14} />
                            {state.status === "uploading"
                                ? "Téléversement…"
                                : value
                                  ? "Remplacer"
                                  : "Téléverser une image"}
                        </button>
                        {value && (
                            <button
                                type="button"
                                onClick={() => onChange("")}
                                className="text-sm text-neutral-400 hover:text-red-400 px-3 py-2 border border-ink-border hover:border-red-500 inline-flex items-center gap-2 transition-colors"
                                data-testid={`${testId}-clear-btn`}
                            >
                                <X size={14} /> Retirer
                            </button>
                        )}
                    </div>
                    {value && (
                        <div className="text-xs text-neutral-500 break-all">
                            {value}
                        </div>
                    )}
                    {state.status === "error" && (
                        <div
                            className="text-sm text-red-300"
                            data-testid={`${testId}-error`}
                        >
                            {state.error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
